import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Chip,
  Paper,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  Clear as ClearIcon,
  DoneAll as DoneAllIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '../utils/locale';
import {
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { storedText } from '../i18n/stored';

const NotificationMenu: React.FC = () => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications } = useNotifications();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    handleClose();
    if ((notification.category === 'support' || notification.category === 'assignment') && notification.ticketId) {
      navigate(`/waha/support-tickets/${notification.ticketId}`);
    } else if (notification.category === 'session') {
      navigate('/waha/dashboard');
    } else if (notification.category === 'assistant') {
      navigate('/ai-assistant/management?tab=numbers');
    } else if (notification.category === 'wacloud') {
      // WhatsApp Business (Cloud API): the conversation with this customer
      // The assistant's notices name the conversation; message notices the customer's WhatsApp id
      navigate(notification.conversationId ? `/messages/${notification.conversationId}` : notification.chatId ? `/messages?wa=${encodeURIComponent(notification.chatId)}` : '/messages');
    } else if (notification.category === 'social') {
      // Instagram / Messenger: the conversation itself
      navigate(notification.commentId ? `/social/comments?c=${notification.commentId}` : notification.threadId ? `/social/inbox/${notification.threadId}` : '/social/inbox');
    } else if (notification.category === 'group_tracker') {
      navigate(notification.workItemId ? `/waha/group-tracker?item=${notification.workItemId}` : '/waha/group-tracker');
    } else if (notification.chatId) {
      navigate(`/waha/chat/${encodeURIComponent(notification.chatId)}`);
    } else {
      navigate('/waha/chat');
    }
  };

  const getNotificationIcon = (notification: any) => {
    switch (notification.category) {
      case 'support':
        return <SupportIcon sx={{ color: theme.palette.warning.main }} />;
      case 'assignment':
        return <SupportIcon sx={{ color: theme.palette.info.main }} />;
      case 'session':
        return <WhatsAppIcon sx={{ color: theme.palette.error.main }} />;
      default:
        return <WhatsAppIcon sx={{ color: '#1976D2' }} />;
    }
  };

  const getCategoryChip = (notification: any) => {
    switch (notification.category) {
      case 'support':
        return <Chip label={t('Destek')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark }} />;
      case 'assignment':
        return <Chip label={t('Atama')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.15), color: theme.palette.info.dark }} />;
      case 'session':
        return <Chip label={t('Bağlantı')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: alpha(theme.palette.error.main, 0.15), color: theme.palette.error.dark }} />;
      case 'group_tracker':
        return <Chip label={t('İş Takibi')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.15), color: theme.palette.info.dark }} />;
      case 'assistant':
        return <Chip label={t('AI Asistan')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.15), color: theme.palette.info.dark }} />;
      default:
        return <Chip label={t('Sohbet')} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: alpha('#1976D2', 0.15), color: '#1565C0' }} />;
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Format phone number for display
    if (phone.startsWith('90')) {
      return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
    }
    return phone;
  };

  return (
    <>
      <Tooltip title={t('Bildirimler')}>
        <IconButton
          onClick={handleClick}
          sx={{
            width: 38,
            height: 38,
            backgroundColor: mode === 'dark'
              ? alpha('#ffffff', 0.05)
              : alpha('#000000', 0.02),
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? alpha('#ffffff', 0.08)
                : alpha('#000000', 0.04),
            }
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 600,
            overflow: 'hidden',
            mt: 1.5,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('Bildirimler')}
              </Typography>
              {unreadCount > 0 && (
                <Chip 
                  label={unreadCount} 
                  size="small" 
                  color="error"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Box>
            {notifications.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={t('Tümünü okundu işaretle')}>
                  <IconButton size="small" onClick={markAllAsRead}>
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('Tümünü temizle')}>
                  <IconButton size="small" onClick={clearNotifications}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        {/* Notifications List */}
        <List sx={{ 
          p: 0, 
          maxHeight: 450, 
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: 6
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.300',
            borderRadius: 1
          }
        }}>
          {notifications.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              color: 'text.secondary'
            }}>
              <NotificationsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
              <Typography variant="body2">
                {t('Henüz bildirim yok')}
              </Typography>
            </Box>
          ) : (
            notifications.slice(0, 20).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    cursor: 'pointer',
                    bgcolor: !notification.read ? alpha('#1976D2', 0.05) : 'transparent',
                    '&:hover': {
                      bgcolor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.common.white, 0.05)
                          : alpha(theme.palette.common.black, 0.02)
                    },
                    position: 'relative'
                  }}
                >
                  {!notification.read && (
                    <CircleIcon
                      sx={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 8,
                        color: notification.category === 'session' ? theme.palette.error.main : notification.category === 'assignment' ? theme.palette.info.main : '#1976D2'
                      }}
                    />
                  )}
                  <ListItemAvatar sx={{ ml: !notification.read ? 1 : 0 }}>
                    <Avatar sx={{
                      bgcolor: notification.category === 'session' ? alpha(theme.palette.error.main, 0.1) : notification.category === 'assignment' ? alpha(theme.palette.info.main, 0.1) : notification.category === 'support' ? alpha(theme.palette.warning.main, 0.1) : alpha('#1976D2', 0.1),
                      width: 40,
                      height: 40
                    }}>
                      {getNotificationIcon(notification)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {storedText(notification.senderName, notification.i18n?.title) || formatPhoneNumber(notification.from || '')}
                        </Typography>
                        {getCategoryChip(notification)}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.primary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mt: 0.5
                          }}
                        >
                          {storedText(notification.message, notification.i18n?.message)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary',
                            mt: 0.5,
                            display: 'block'
                          }}
                        >
                          {formatDistanceToNow(new Date(notification.timestamp), { 
                            addSuffix: true, 
                            locale: getDateFnsLocale() 
                          })}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))
          )}
        </List>

        {/* Footer */}
        {notifications.length > 0 && (
          <Box sx={{ 
            p: 1.5, 
            borderTop: 1, 
            borderColor: 'divider',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'
          }}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                handleClose();
                navigate('/waha/chat');
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('Tüm mesajları görüntüle')}
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationMenu;